import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import CustomDatePicker from '@shared/components/CustomDatePicker';
import LoadingButton from '@shared/components/LoadingButton';
import Table from '@shared/components/Table';
import PersonPicker from '@shared/components/PersonPicker';
import { nomenclaturesOz } from '@modules/organizationalStructure/nomenclatures/services/nomenclaturesOz';
import { levelOfStructureService } from '@modules/organizationalStructure/levelOfStructure/services/levelOfStructure';
import { majorContractsService } from '@shared/services/majorContracts';
import { identificatorsService } from '@modules/additionalSettings/nomenclatures/services/identificators';
import { noticePeriodService } from '@modules/staff/noticePeriod/services/noticePeriod';
import { signeesService } from '@modules/organizationalStructure/signees/services/signees';
import { companiesService } from '@shared/services/companiesService';
import { useForm } from 'react-hook-form';
import { notifySuccess } from '@config/tostifyConfig/tostifyConfig';

export default function PersonContractBody({ contractType = 'laborContract', person = null, selectedMainContract = '', mainContracts = [], setContractType, setSelectedMainContract, onNewIconClick,
    setIsEditMode: setParentEditMode, setIsCreateModeActive: setParentCreateModeActive, isCreateModeActive: parentCreateModeActive, setEditingContractId: setParentEditingContractId,
    selectedContractId = null,
    setSelectedContractId,
    setSelectedContractPositionTypeId,
    contractRefreshToken = 0,
    initialSelectedContractId = null,
    contractNo: contractNoProp, setContractNo: setContractNoProp, onClose }) {
    const { t } = useTranslation();
    const { register, handleSubmit, setValue, watch, unregister, reset, formState: { errors } } = useForm({
        defaultValues: {
            contractDate: new Date(),
            effectiveDate: new Date(),
            salary: 0,
            prcntClass: 0,
            durationDays: 1,
            daysAgreedPaidAnnualLeave: 20,
            expiredContract: '2'
        }
    });
    const reasonFieldName = useMemo(() => {
        switch (contractType) {
            case 'laborContract':
            case 'additionalLaborContract':
                return 'hrContractLawReasonsId';
            case 'employerChange':
            case 'additionalAgreement':
                return 'hrAnexLawReasonsId';
            case 'termination':
                return 'hrLeaveLawReasonsId';
            default:
                return 'hrContractLawReasonsId';
        }
    }, [contractType]);

    // Unregister other reason fields when contractType changes
    useEffect(() => {
        const allReasonFields = ['hrContractLawReasonsId', 'hrAnexLawReasonsId', 'hrLeaveLawReasonsId'];
        allReasonFields.forEach(field => {
            if (field !== reasonFieldName) {
                unregister(field);
            }
        });
    }, [contractType, reasonFieldName, unregister]);

    const [documentDate, setDocumentDate] = useState(new Date());
    const [fromDate, setFromDate] = useState(new Date());
    const [contractNoFallback, setContractNoFallback] = useState('');
    const contractNo = contractNoProp ?? contractNoFallback;
    const setContractNo = setContractNoProp ?? setContractNoFallback;
    const [showPositions, setShowPositions] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isCreateModeActive, setIsCreateModeActive] = useState(false); // Track if New icon was clicked
    const [editingContractId, setEditingContractId] = useState(null); // Track which contract is being edited
    const [pendingReasonData, setPendingReasonData] = useState(null); // Store reason data to set after majorContracts load
    const [hasInitialLoad, setHasInitialLoad] = useState(false); // Track if initial contract load has been done
    const initialLoadInProgressRef = useRef(false); // Prevent duplicated initial loads
    const [serverErrors, setServerErrors] = useState([]); // Backend / general errors for this form
    const [showErrorModal, setShowErrorModal] = useState(false); // Controls console-style error modal visibility

    const scrollModalToTop = () => {
        const wrapper = document.querySelector('.person-form-wrapper');
        if (wrapper && typeof wrapper.scrollTo === 'function') {
            wrapper.scrollTop = 0;
            wrapper.scrollTo({ top: 0, behavior: 'auto' });
        } else {
            window.scrollTo({ top: 0, behavior: 'auto' });
        }
    };

    const openErrorModalAfterScroll = () => {
        scrollModalToTop();
        setTimeout(() => setShowErrorModal(true), 0);
    };

    useEffect(() => {
        if (typeof document === 'undefined') return;

        if (showErrorModal) {
            document.documentElement.classList.add('modal-open');
            document.body.classList.add('modal-open');
        } else {
            document.documentElement.classList.remove('modal-open');
            document.body.classList.remove('modal-open');
        }

        return () => {
            document.documentElement.classList.remove('modal-open');
            document.body.classList.remove('modal-open');
        };
    }, [showErrorModal]);

    // Computed value to determine if form is in edit mode
    const isEditMode = isCreateModeActive || !!editingContractId;

    // Notify parent component of edit mode changes
    useEffect(() => {
        if (setParentEditMode) {
            setParentEditMode(isEditMode);
        }
    }, [isEditMode, setParentEditMode]);

    // Notify parent component of create mode changes
    useEffect(() => {
        if (setParentCreateModeActive) {
            setParentCreateModeActive(isCreateModeActive);
        }
    }, [isCreateModeActive, setParentCreateModeActive]);

    // Notify parent component of editing contract ID changes
    useEffect(() => {
        if (setParentEditingContractId) {
            setParentEditingContractId(editingContractId);
        }
    }, [editingContractId, setParentEditingContractId]);

    // Get default form values for create mode
    const getDefaultFormValues = () => {
        const today = new Date();
        return {
            contractDate: today,
            effectiveDate: today,
            salary: 0,
            prcntClass: 0,
            durationDays: 8,
            daysAgreedPaidAnnualLeave: 20,
            memberBoard: '2', // No
            isBlockedSalaryAmount: '2', // No
            testContract: '2', // No
            boardDirectors: '2', // No
            expiredContract: '2', // No
            [reasonFieldName]: '' // Reason field based on contract type
        };
    };

    // Reset form to default values when entering create mode (not edit mode)
    useEffect(() => {
        // Only reset when creating new, not when editing existing
        if (isCreateModeActive && !editingContractId) {
            const defaultValues = getDefaultFormValues();
            reset(defaultValues);

            // Reset separate state variables
            const today = new Date();
            setDocumentDate(today);
            setFromDate(today);
            setContractNo('');
            setReasonNAP('');
            setIsExpiredContractEditable(false);

            // Reset formData to empty/default values
            setFormData({
                companyBranch: '',
                companyBranchId: '',
                costCenter: '',
                structuralGroup: '',
                positionInTheCompany: '',
                location: '',
                positionsUnderNOC: '',
                personnelCategory: '',
                workCategory: '',
                insuaranceType: '',
                noticePeriod: '',
                status: '',
                signatures1: '',
                signatures2: '',
                positionType: '',
                nkid: ''
            });
        }
    }, [isCreateModeActive, editingContractId, reset, reasonFieldName]);

    const [positionsData, setPositionsData] = useState([]);
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [showPositionsTable, setShowPositionsTable] = useState(false);
    const [currentView, setCurrentView] = useState('structuralGroups');

    // Structural groups table state
    // Get adminBranchId from Redux store instead of fetching all companies
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [levels, setLevels] = useState([]);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [levelsLoading, setLevelsLoading] = useState(false);
    const [nomenclatures, setNomenclatures] = useState([]);
    const [nomenclaturesLoading, setNomenclaturesLoading] = useState(false);
    const [majorContracts, setMajorContracts] = useState([]);
    const [majorContractsLoading, setMajorContractsLoading] = useState(false);
    const [typeOfInsurance, setTypeOfInsurance] = useState([]);
    const [typeOfInsuranceLoading, setTypeOfInsuranceLoading] = useState(false);
    const [noticePeriods, setNoticePeriods] = useState([]);
    const [noticePeriodsLoading, setNoticePeriodsLoading] = useState(false);
    const [signees, setSignees] = useState([]);
    const [signeesLoading, setSigneesLoading] = useState(false);
    const [reasonNAP, setReasonNAP] = useState('');
    const [isExpiredContractEditable, setIsExpiredContractEditable] = useState(false);
    // Track selected reason for dynamic companies behavior
    const [reasonCode, setReasonCode] = useState('');
    const [reasonId, setReasonId] = useState('');
    const [showAllCompanies, setShowAllCompanies] = useState(false);
    // Holder person (Титуляр на позицията) picker state
    const [holderPerson, setHolderPerson] = useState(null);
    const [isHolderPickerOpen, setIsHolderPickerOpen] = useState(false);
    const [holderPersons, setHolderPersons] = useState([]);
    const [holderLoading, setHolderLoading] = useState(false);
    const [holderPageNumber, setHolderPageNumber] = useState(0);
    const [holderPageSize, setHolderPageSize] = useState(10);
    const [holderTotalPages, setHolderTotalPages] = useState(0);
    const [holderTotalElements, setHolderTotalElements] = useState(0);
    const [holderSearchQuery, setHolderSearchQuery] = useState({});
    const [holderSearchType, setHolderSearchType] = useState('name');
    const [contracts, setContracts] = useState([]);
    const [contractsLoading, setContractsLoading] = useState(false);
    const [structuralGroups, setStructuralGroups] = useState([]);
    const [structuralGroupsLoading, setStructuralGroupsLoading] = useState(false);
    const [drillDownData, setDrillDownData] = useState([]);
    const [isDrillDownMode, setIsDrillDownMode] = useState(false);
    const [parentId, setParentId] = useState(null);
    const [breadcrumbPath, setBreadcrumbPath] = useState([]);
    const [checkboxSelectedRecord, setCheckboxSelectedRecord] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);

    // Form fields state
    const [formData, setFormData] = useState({
        companyBranch: '',
        companyBranchId: '',
        costCenter: '',
        structuralGroup: '',
        positionInTheCompany: '',
        location: '',
        positionsUnderNOC: '',
        personnelCategory: '',
        workCategory: '',
        insuaranceType: '',
        noticePeriod: '',
        status: '',
        signatures1: '',
        signatures2: '',
        positionType: '',
        nkid: '' // Add nkid to formData
    });

    const columns = [{
        key: 'type',
        label: t('persons.personContract.type'),
    }, {
        key: 'branch',
        label: t('persons.personContract.branch'),
    }, {
        key: 'docNumber',
        label: t('persons.personContract.docNumber'),
    }, {
        key: 'docDate',
        label: t('persons.personContract.docDate'),
    }, {
        key: 'fromDate',
        label: t('persons.personContract.fromDate'),
    }, {
        key: 'codeNap',
        label: t('persons.personContract.codeNap'),
    }, {
        key: 'status',
        label: t('persons.personContract.currency'),
    }, {
        key: 'approved',
        label: t('persons.personContract.approved'),
    }
    ];

    // Map typeDoc to readable type
    const getTypeDocLabel = (typeDoc, reasonCode) => {
        // Check for additional labor contract (typeDoc 1 with reasonCode 010 or 011)
        if (typeDoc === "1" && (reasonCode === "010" || reasonCode === "011")) {
            return t('persons.personContract.additionalLaborContract') || 'Допълнителен трудов договор';
        }

        switch (typeDoc) {
            case '1':
                return t('persons.personContract.laborContract') || 'Трудов договор';
            case '2':
                return t('persons.personContract.additionalAgreement') || 'Допълнително споразумение';
            case '3':
                return t('persons.personContract.termination') || 'Прекратяване';
            case '9':
                return t('persons.personContract.employerChange') || 'Промяна на работодател';
            default:
                return typeDoc || '';
        }
    };

    const getNoiCodeLabel = (noiCode) => {
        switch (noiCode) {
            case 0:
                return t('persons.personContract.noiCode1') || "Редовни данни";
            case 1:
                return t('persons.personContract.noiCode2') || "Корекция";
            case 3:
                return t('persons.personContract.noiCode3') || "Заличаване";
            default:
                return t('persons.personContract.noiCode4') || 'Не уведомява НАП';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 0:
                return t('persons.personContract.confirmed') || "Потвърден";
            case 1:
                return t('persons.personContract.rejected') || "Отказан";
            case 2:
                return t('persons.personContract.waitingForResponse') || "Чака отговор";
            case "W":
                return t('persons.personContract.inactive') || "Неактивен"
        }
    };


    // Map API response to table data format
    const mappedContracts = contracts.map(contract => ({
        ...contract, // Keep original contract data for access to contractId
        type: getTypeDocLabel(contract.typeDoc, contract.reasonCode),
        branch: contract.branchName || '',
        docNumber: contract.contractNo || '',
        docDate: contract.contractDate || '',
        fromDate: contract.effectiveDate || '',
        codeNap: getNoiCodeLabel(contract.noiCode) || '',
        status: getStatusLabel(contract.status) || ''
    }));

    // Load contract by ID - single source of truth (not index-based)
    const loadContractById = async (contractId, isEditMode = false) => {
        if (!contractId) {
            console.warn('No contractId provided');
            return;
        }

        // Notify parent of currently loaded contract (for overlay e.g. change document number)
        if (setSelectedContractId) {
            setSelectedContractId(contractId);
        }

        // Reset edit mode if not explicitly editing
        if (!isEditMode) {
            setIsCreateModeActive(false);
            setEditingContractId(null);
        }

        // Reset special reason-dependent UI when switching contracts
        setReasonCode('');
        setReasonId('');
        setHolderPerson(null);

        try {
            setLoading(true);
            const response = await majorContractsService.getContractById(contractId);

            if (response) {
                setSelectedContractPositionTypeId?.(response.positionTypeId ?? null);
                // Set contractType based on typeDoc
                if (response.typeDoc === "1" && (response.reasonCode === "010" || response.reasonCode === "011")) {
                    setContractType('additionalLaborContract');
                } else if (response.typeDoc === '1') {  // Change this to else if
                    setContractType('laborContract');
                } else if (response.typeDoc === '2') {
                    setContractType('additionalAgreement');
                } else if (response.typeDoc === '3') {
                    setContractType('termination');
                } else if (response.typeDoc === '9') {
                    setContractType('employerChange');
                }

                // Set dates
                if (response.contractDate) {
                    const contractDate = new Date(response.contractDate);
                    setDocumentDate(contractDate);
                    setValue('contractDate', contractDate);
                }
                if (response.effectiveDate) {
                    const effectiveDate = new Date(response.effectiveDate);
                    setFromDate(effectiveDate);
                    setValue('effectiveDate', effectiveDate);
                }

                // Set reason fields based on typeDoc
                if (response.typeDoc === '1' && response.hrContractLawReasonsId) {
                    setPendingReasonData({
                        fieldName: 'hrContractLawReasonsId',
                        reasonId: response.hrContractLawReasonsId,
                        reasonName: response.hrContractLawReasonsName || ''
                    });
                } else if ((response.typeDoc === '2' || response.typeDoc === '9') && response.hrAnexLawReasonsId) {
                    setPendingReasonData({
                        fieldName: 'hrAnexLawReasonsId',
                        reasonId: response.hrAnexLawReasonsId,
                        reasonName: response.hrAnexLawReasonsName || ''
                    });
                } else if (response.typeDoc === '3' && response.hrLeaveLawReasonsId) {
                    setPendingReasonData({
                        fieldName: 'hrLeaveLawReasonsId',
                        reasonId: response.hrLeaveLawReasonsId,
                        reasonName: response.hrLeaveLawReasonsName || ''
                    });
                } else {
                    setPendingReasonData(null);
                }

                // Set document number
                if (response.contractNo) {
                    setContractNo(response.contractNo);
                    setValue('YOUR_DOCUMENT_NUMBER_FIELD_NAME', response.contractNo);
                }

                if (response.durationDays !== undefined && response.durationDays !== null) {
                    setValue('durationDays', response.durationDays);
                }
                if (response.daysAgreedPaidAnnualLeave !== undefined && response.daysAgreedPaidAnnualLeave !== null) {
                    setValue('daysAgreedPaidAnnualLeave', response.daysAgreedPaidAnnualLeave);
                }
                if (response.memberBoard !== undefined) setValue('memberBoard', response.memberBoard ? '1' : '2');
                if (response.blockedSalaryAmount !== undefined) setValue('isBlockedSalaryAmount', response.blockedSalaryAmount ? '1' : '2');
                if (response.salary !== undefined && response.salary !== null) {
                    setValue('salary', response.salary);
                }
                if (response.boardDirectors !== undefined) setValue('boardDirectors', response.boardDirectors ? '1' : '2');
                if (response.prcntClass !== undefined && response.prcntClass !== null) {
                    setValue('prcntClass', response.prcntClass);
                }

                // Set position/organizational fields
                if (response.locationName) {
                    setFormData(prev => ({ ...prev, location: response.locationName }));
                    if (response.locationId) setValue('locationId', response.locationId);
                }
                if (response.costCenterName) {
                    setFormData(prev => ({ ...prev, costCenter: response.costCenterName }));
                    setValue('empOfficesId', response.costCenterId);
                }
                if (response.hrOrgUnitName) {
                    setFormData(prev => ({ ...prev, structuralGroup: response.hrOrgUnitName }));
                    setValue('hrOrgUnitId', response.hrOrgUnitId);
                }
                if (response.empBranchName) {
                    setFormData(prev => ({ ...prev, companyBranch: response.empBranchName }));
                    setValue('adminBranch', response.empBranchId);
                    setValue('contractBranchId', response.empBranchId);
                }
                if (response.positionName) {
                    setFormData(prev => ({ ...prev, positionInTheCompany: response.positionName }));
                    const resolvedPositionId = response.hrPositionId ?? response.positionId ?? '';
                    setValue('hrPositionId', resolvedPositionId);
                }
                if (response.nkdpName) {
                    setFormData(prev => ({ ...prev, positionsUnderNOC: response.nkdpName }));
                    if (response.nkpdId != null) {
                        setValue('nkdp', response.nkpdId);
                    } else if (response.nkdp != null) {
                        setValue('nkdp', response.nkdp);
                    }
                }
                if (response.catWorkName) {
                    setFormData(prev => ({ ...prev, workCategory: response.catWorkName }));
                    if (response.catWork) setValue('catWork', response.catWork);
                }
                if (response.catPersName) {
                    setFormData(prev => ({ ...prev, personnelCategory: response.catPersName }));
                    if (response.catPers) setValue('catPers', response.catPers);
                }
                // If backend returns titularId equal to current person, pre-fill holderPerson for display
                if (response.titularId && person) {
                    const currentId = person.personId || person.id;
                    if (String(response.titularId) === String(currentId)) {
                        const fullNamePerson = {
                            name: person.name,
                            fathersName: person.fathersName,
                            surname: person.surname,
                        };
                        setHolderPerson(fullNamePerson);
                    }
                }
                if (response.kindInsuranceId) {
                    // response.kindInsuranceId is the value (e.g., 12), find by value first
                    const insuranceType = typeOfInsurance.find(ins => {
                        // Try to match by value first (response contains value)
                        const matchByValue = ins.value === response.kindInsuranceId || String(ins.value) === String(response.kindInsuranceId);
                        // Also check by id in case response contains id
                        const matchById = ins.id === response.kindInsuranceId || String(ins.id) === String(response.kindInsuranceId);
                        return matchByValue || matchById;
                    });
                    if (insuranceType) {
                        // Set id in formData for select to work (select options use id as value)
                        setFormData(prev => ({ ...prev, insuaranceType: insuranceType.id }));
                        // Set id in form field (will be converted to value on submit)
                        setValue('kindInsuranceId', insuranceType.id);
                    }
                }
                if (response.hrTerminationNotePeriodId) {
                    setFormData(prev => ({ ...prev, noticePeriod: response.hrTerminationNotePeriodId }));
                    setValue('hrTerminationNotePeriodId', response.hrTerminationNotePeriodId);
                }
                if (response.statut) {
                    setFormData(prev => ({ ...prev, status: response.statut }));
                    setValue('statut', response.statut);
                }
                if (response.hrLabourDocSignBySignatoryId) {
                    setFormData(prev => ({ ...prev, signatures1: response.hrLabourDocSignBySignatoryId }));
                    setValue('hrLabourDocSignBySignatoryId', response.hrLabourDocSignBySignatoryId);
                }
                if (response.hrLabourDocSignBySignatoryId2) {
                    setFormData(prev => ({ ...prev, signatures2: response.hrLabourDocSignBySignatoryId2 }));
                    setValue('hrLabourDocSignBySignatoryId2', response.hrLabourDocSignBySignatoryId2);
                }
                if (response.positionTypeId) {
                    setFormData(prev => ({ ...prev, positionType: response.positionTypeId }));
                    setValue('positionTypeId', response.positionTypeId);
                }

                if (response.baseContractId && setSelectedMainContract) {
                    setSelectedMainContract(response.baseContractId);
                }

                // Show the form
                setShowPositions(true);
            } else {
                setSelectedContractPositionTypeId?.(null);
            }
        } catch (error) {
            setSelectedContractPositionTypeId?.(null);
            console.error('Error fetching contract data:', error);
            const backendMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');

            let normalized = [];
            if (Array.isArray(backendMessage)) {
                normalized = backendMessage;
            } else if (backendMessage) {
                normalized = String(backendMessage).split(/\r?\n/);
            }

            const cleaned = normalized
                .map(msg => String(msg).trim())
                .filter(msg => msg.length > 0);

            setServerErrors(prev => [...prev, ...cleaned]);
            openErrorModalAfterScroll();
        } finally {
            setLoading(false);
        }
    };

    // Handle row click to load contract data for editing (uses index for backward compatibility)
    const handleContractRowClick = async (index, isEditMode = false) => {
        if (isCreateModeActive && !isEditMode) {
            return;
        }

        const currentData = mappedContracts;
        if (index >= 0 && index < currentData.length) {
            const contract = currentData[index];
            const contractId = contract.contractId;

            if (!contractId) {
                console.warn('No contractId found');
                return;
            }

            // Use ID-based loading instead of index
            await loadContractById(contractId, isEditMode);
        }
    };

    const searchOptions = [
        {
            key: 'name',
            label: 'name',
        },
        {
            key: 'code',
            label: 'code',
        },
        {
            key: 'structuralGroupDate',
            label: 'structuralGroupDate',
            type: 'date',
        },
    ];
    // Structural groups data start
    // Structural groups table columns
    const structuralGroupsColumns = [
        { key: 'hrOrgUnitLevelsId', label: t('organizationalStructure.structuralGroups.table.headers.level') },
        { key: 'code', label: t('organizationalStructure.structuralGroups.table.headers.code') },
        { key: 'longDescription', label: t('organizationalStructure.structuralGroups.table.headers.name') },
    ];

    const positionsColumns = [
        { key: 'positionNumber', label: t('organizationalStructure.structuralGroups.table.headers.number') || 'No.' },
        { key: 'positionName', label: t('organizationalStructure.structuralGroups.table.headers.name') || 'Title' },
        { key: 'ocupantName', label: t('organizationalStructure.structuralGroups.table.headers.occupyingPosition') || 'Occupying position' }
    ];

    // Helper: fetch only admin branch company (original behavior)
    const fetchCompaniesForAdminBranch = async () => {
        if (!adminBranchId) return;
        setSelectedCompany(adminBranchId);
        try {
            setCompaniesLoading(true);
            const company = await companiesService.getSelectedCompany(adminBranchId);
            if (company) {
                setCompanyOptions([{ id: company.id, name: company.name || company.branchName || '' }]);
            }
        } catch (err) {
            console.error('Error fetching current company:', err);
        } finally {
            setCompaniesLoading(false);
        }
    };

    // Helper: fetch all companies
    const fetchAllCompanies = async () => {
        try {
            setCompaniesLoading(true);
            const companies = await companiesService.getAllCompanies();
            const options = (companies || []).map(c => ({
                id: c.id,
                name: c.name || c.branchName || ''
            }));
            setCompanyOptions(options);
        } catch (err) {
            console.error('Error fetching companies:', err);
        } finally {
            setCompaniesLoading(false);
        }
    };

    // Simple rule:
    // - Default: fetch adminBranchId company
    // - If reasonId === 1011 && reasonCode === 011: fetch ALL companies
    useEffect(() => {
        const fetchCurrentCompany = async () => {
            if (reasonCode === '011' && String(reasonId) === '1011') {
                setShowAllCompanies(true);
                await fetchAllCompanies();
            } else {
                setShowAllCompanies(false);
                await fetchCompaniesForAdminBranch();
            }
        };

        if (adminBranchId !== null) {
            fetchCurrentCompany();
        }
    }, [adminBranchId, reasonCode, reasonId]);

    // Fetch levels when company changes
    useEffect(() => {
        const fetchLevels = async () => {
            if (selectedCompany) {
                try {
                    setLevelsLoading(true);
                    const response = await levelOfStructureService.getAllLevels(selectedCompany);
                    setLevels(response || []);
                    if (response && response.length > 0) {
                        const levelWithPlanLevel0 = response.find(level => level.planLevel === 0);
                        if (levelWithPlanLevel0) {
                            setSelectedLevel(levelWithPlanLevel0.id);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching levels:', error);
                    setLevels([]);
                } finally {
                    setLevelsLoading(false);
                }
            } else {
                setLevels([]);
                setSelectedLevel(null);
            }
        };
        const fetchNomenclatures = async () => {
            try {
                setNomenclaturesLoading(true);
                const response = await nomenclaturesOz.getNomenclatureItems(9, "true");
                setNomenclatures(response || []);
            } catch (error) {
                console.error('Error fetching nomenclatures:', error);
                setNomenclatures([]);
            } finally {
                setNomenclaturesLoading(false);
            }
        };
        const fetchMajorContracts = async () => {
            try {
                setMajorContractsLoading(true);
                let response = [];

                // Fetch based on contract type
                switch (contractType) {
                    case 'laborContract':
                        response = await majorContractsService.getLaborContractReasons();
                        break;
                    case 'additionalLaborContract':
                        response = await majorContractsService.getAdditionalLaborContractReasons();
                        break;
                    case 'additionalAgreement':
                        response = await majorContractsService.getAdditionalAgreementReasons();
                        break;
                    case 'termination':
                        response = await majorContractsService.getTerminationReasons();
                        break;
                    case 'employerChange':
                        response = await majorContractsService.getAdditionalAgreementReasons();
                        break;
                    default:
                        response = await majorContractsService.getTerminationReasons();
                        break;
                }
                setMajorContracts(response || []);
            } catch (error) {
                console.error('Error fetching major contracts:', error);
                setMajorContracts([]);
            } finally {
                setMajorContractsLoading(false);
            }
        };
        const fetchTypeOfInsurance = async () => {
            try {
                setTypeOfInsuranceLoading(true);
                const response = await identificatorsService.getTypeOfInsurance();
                setTypeOfInsurance(response || []);
            } catch (error) {
                console.error('Error fetching type of insurance:', error);
                setTypeOfInsurance([]);
            } finally {
                setTypeOfInsuranceLoading(false);
            }
        };
        const fetchNoticePeriods = async () => {
            try {
                setNoticePeriodsLoading(true);
                const response = await noticePeriodService.getNoticePeriodsList();
                setNoticePeriods(response || []);
            } catch (error) {
                console.error('Error fetching notice periods:', error);
                setNoticePeriods([]);
            } finally {
                setNoticePeriodsLoading(false);
            }
        };
        const fetchSignees = async () => {
            if (selectedCompany) {
                try {
                    setSigneesLoading(true);
                    const response = await signeesService.getSigneesByCompany(selectedCompany);
                    setSignees(response || []);
                } catch (error) {
                    console.error('Error fetching signees:', error);
                    setSignees([]);
                } finally {
                    setSigneesLoading(false);
                }
            } else {
                setSignees([]);
            }
        };
        fetchSignees();
        fetchTypeOfInsurance();
        fetchNoticePeriods();
        fetchNomenclatures();
        fetchMajorContracts();
        fetchLevels();
    }, [selectedCompany, contractType]);

    // Set reason field value after majorContracts are loaded and contractType is set
    useEffect(() => {
        if (pendingReasonData && !majorContractsLoading && majorContracts.length > 0 && reasonFieldName === pendingReasonData.fieldName) {
            const selectedContract = majorContracts.find(mc =>
                mc.id === Number(pendingReasonData.reasonId) || String(mc.id) === String(pendingReasonData.reasonId)
            );
            const reasonExists = !!selectedContract;

            if (reasonExists) {
                setValue(pendingReasonData.fieldName, pendingReasonData.reasonId);
                setReasonNAP(pendingReasonData.reasonName);
                // Sync reasonCode / reasonId for special UI when loading existing contracts
                const code = selectedContract && selectedContract.reasonCode != null
                    ? String(selectedContract.reasonCode).padStart(3, '0')
                    : '';
                const id = selectedContract && selectedContract.reasonId != null
                    ? String(selectedContract.reasonId)
                    : '';
                setReasonCode(code);
                setReasonId(id);
                const isFixedTermReason = selectedContract?.description === '2 Срочен трудов договор по чл.68,ал.1,т.1 КТ';
                setValue('expiredContract', isFixedTermReason ? '1' : '2');
                setIsExpiredContractEditable(isFixedTermReason);
                setPendingReasonData(null);
            }
        }
    }, [pendingReasonData, majorContractsLoading, majorContracts, reasonFieldName, setValue]);

    // Fetch structural groups when level changes
    useEffect(() => {
        const fetchStructuralGroups = async () => {
            if (selectedLevel) {
                try {
                    setStructuralGroupsLoading(true);
                    const response = await levelOfStructureService.getOrgUnitsByLevel(selectedLevel);
                    setStructuralGroups(response || []);
                } catch (error) {
                    console.error('Error fetching structural groups:', error);
                    setStructuralGroups([]);
                } finally {
                    setStructuralGroupsLoading(false);
                }
            } else {
                setStructuralGroups([]);
            }
            setIsDrillDownMode(false);
            setDrillDownData([]);
            setParentId(null);
            setBreadcrumbPath([]);
            setCheckboxSelectedRecord(null);
            setSelectedRecord(null);
        };
        fetchStructuralGroups();
    }, [selectedLevel]);

    const selectedLevelObject = levels.find(level => level.id === selectedLevel);

    const handleCompanyChange = (companyId) => {
        setSelectedCompany(companyId);
    };

    const handleLevelChange = (levelId) => {
        setSelectedLevel(levelId);
    };

    // Fetch contracts function
    const fetchContracts = async () => {
        if (person && person.recordId) {
            try {
                setContractsLoading(true);
                const response = await majorContractsService.getAdditionalAgreementContractsById(person.recordId);
                setContracts(response || []);
            } catch (error) {
                console.error('Error fetching contracts:', error);
                setContracts([]);
            } finally {
                setContractsLoading(false);
            }
        } else {
            setContracts([]);
        }
    };

    // Fetch contracts when person changes
    useEffect(() => {
        fetchContracts();
    }, [person?.recordId]);

    // Refresh contracts list, loaded contract form, and positions table after heading actions.
    useEffect(() => {
        if (!contractRefreshToken) {
            return;
        }

        const refreshAfterContractAction = async () => {
            let updatedContracts = [];

            if (person?.recordId) {
                try {
                    setContractsLoading(true);
                    updatedContracts = await majorContractsService.getAdditionalAgreementContractsById(person.recordId);
                    setContracts(updatedContracts || []);
                } catch (error) {
                    console.error('Error refreshing contracts:', error);
                    setContracts([]);
                } finally {
                    setContractsLoading(false);
                }
            }

            const contractIdToLoad = editingContractId || selectedContractId;
            const contractStillExists = contractIdToLoad && (updatedContracts || []).some(
                (contract) => String(contract.contractId) === String(contractIdToLoad)
            );

            if (contractStillExists) {
                await loadContractById(contractIdToLoad, !!editingContractId);
            } else if ((updatedContracts || []).length > 0) {
                await loadContractById(updatedContracts[updatedContracts.length - 1].contractId);
            }

            if (currentView === 'positions' && selectedRecord?.id) {
                try {
                    setPositionsLoading(true);
                    const response = await levelOfStructureService.getPositionsForContract(selectedRecord.id);
                    setPositionsData(response || []);
                } catch (error) {
                    console.error('Error refreshing positions:', error);
                    setPositionsData([]);
                } finally {
                    setPositionsLoading(false);
                }
            }
        };

        refreshAfterContractAction();
    }, [contractRefreshToken]);

    // Reset initial-load guards when switching person.
    useEffect(() => {
        setHasInitialLoad(false);
        initialLoadInProgressRef.current = false;
    }, [person?.recordId]);

    useEffect(() => {
        if (!(contracts && contracts.length > 0) || contractsLoading || hasInitialLoad || initialLoadInProgressRef.current || typeOfInsuranceLoading || typeOfInsurance.length === 0) {
            return;
        }

        const restoreContract = async () => {
            initialLoadInProgressRef.current = true;
            const restoredById = initialSelectedContractId
                ? contracts.find((contract) => String(contract.contractId) === String(initialSelectedContractId))
                : null;
            const selectedContract = selectedMainContract
                ? contracts.find((contract) => String(contract.contractId) === String(selectedMainContract))
                : null;
            // Default open behavior: load the last row from table.
            const contractToLoad = restoredById || selectedContract || contracts[contracts.length - 1];

            if (contractToLoad?.contractId) {
                try {
                    await loadContractById(contractToLoad.contractId);
                    setHasInitialLoad(true);
                } finally {
                    initialLoadInProgressRef.current = false;
                }
            } else {
                initialLoadInProgressRef.current = false;
            }
        };

        restoreContract();
    }, [contracts, contractsLoading, hasInitialLoad, typeOfInsuranceLoading, typeOfInsurance, selectedMainContract, initialSelectedContractId]);

    useEffect(() => {
        if (onNewIconClick !== undefined && onNewIconClick > 0) {
            setIsCreateModeActive(true);
        }
    }, [onNewIconClick]);

    // Sync with parent's isCreateModeActive state (for cancel functionality)
    useEffect(() => {
        if (parentCreateModeActive !== undefined && parentCreateModeActive !== isCreateModeActive) {
            setIsCreateModeActive(parentCreateModeActive);
            if (!parentCreateModeActive) {
                setEditingContractId(null);
                // When exiting create mode, clear special reason-dependent UI
                setReasonCode('');
                setReasonId('');
                setHolderPerson(null);
            }
        }
    }, [parentCreateModeActive]);



    const handleRowClick = async (index) => {
        const currentData = isDrillDownMode ? drillDownData : structuralGroups;

        if (index >= 0 && index < currentData.length) {
            const structuralGroup = currentData[index];

            try {
                setStructuralGroupsLoading(true);
                const response = await levelOfStructureService.getOrgUnitById(selectedCompany, { parentId: structuralGroup.id });

                setDrillDownData(response || []);
                setIsDrillDownMode(true);
                setSelectedRecord(structuralGroup);
                setCheckboxSelectedRecord(null);
                setParentId(structuralGroup.id);
                setBreadcrumbPath([...breadcrumbPath, {
                    id: structuralGroup.id,
                    title: structuralGroup.longDescription || structuralGroup.code,
                    level: structuralGroup.level
                }]);
            } catch (err) {
                console.error('Error fetching drill-down data:', err);
                setDrillDownData([]);
                setIsDrillDownMode(false);
            } finally {
                setStructuralGroupsLoading(false);
            }
        }
    };

    const handleBreadcrumbClick = async (breadcrumbItem, index) => {
        try {
            if (breadcrumbItem.id === 'root' || index === 0) {
                setIsDrillDownMode(false);
                setDrillDownData([]);
                setParentId(null);
                setBreadcrumbPath([]);
                setCheckboxSelectedRecord(null);
                setSelectedRecord(null);
                if (selectedLevel) {
                    setStructuralGroupsLoading(true);
                    const response = await levelOfStructureService.getOrgUnitsByLevel(selectedLevel);
                    setStructuralGroups(response || []);
                    setStructuralGroupsLoading(false);
                }
            } else {
                const actualIndex = index - 1;
                const targetItem = breadcrumbPath[actualIndex];

                if (targetItem) {
                    setStructuralGroupsLoading(true);
                    const response = await levelOfStructureService.getOrgUnitById(selectedCompany, { parentId: targetItem.id });
                    setDrillDownData(response || []);
                    setIsDrillDownMode(true);
                    setSelectedRecord(targetItem);
                    setCheckboxSelectedRecord(null);
                    setParentId(targetItem.id);
                    setBreadcrumbPath(breadcrumbPath.slice(0, actualIndex + 1));
                    setStructuralGroupsLoading(false);
                }
            }
        } catch (err) {
            console.error('Error navigating to breadcrumb level:', err);
            setStructuralGroupsLoading(false);
        }
    };

    const handleCheckboxChange = (index) => {
        const currentData = isDrillDownMode ? drillDownData : structuralGroups;

        if (index >= 0 && index < currentData.length) {
            const structuralGroup = currentData[index];
            const newCheckboxSelection = checkboxSelectedRecord?.id === structuralGroup.id ? null : structuralGroup;
            setCheckboxSelectedRecord(newCheckboxSelection);
            setSelectedRecord(newCheckboxSelection);
        }
    };

    // End of Structural groups data

    const handlePositionIconClick = async () => {
        const orgUnitId = selectedRecord?.id;

        if (!orgUnitId) {
            return;
        }
        try {
            setPositionsLoading(true);
            const response = await levelOfStructureService.getPositionsForContract(orgUnitId);
            setPositionsData(response || []);
            setCurrentView('positions'); // Switch to positions view
        } catch (error) {
            console.error('Error fetching positions for contract:', error);
            setPositionsData([]);
        } finally {
            setPositionsLoading(false);
        }
    }

    const handleBackToStructuralGroups = () => {
        setCurrentView('structuralGroups');
        setPositionsData([]);
    };

    const handlePositionRowIconClick = async (item, index) => {
        if (!item?.id) {
            console.warn('No position id found');
            return;
        }
        try {
            setLoading(true);
            const response = await levelOfStructureService.getPositionByIdForContract(item.id);

            if (response) {
                // Populate form fields with response data
                const newFormData = {
                    companyBranch: response.empBranchName || '',
                    companyBranchId: response.empBranchId || '',
                    costCenter: response.costCenterName || '',
                    costCenterId: response.costCenterId || '',
                    structuralGroup: response.hrOrgUnitName || '',
                    structuralGroupId: response.hrOrgUnitId || '',
                    positionInTheCompany: response.positionName || '',
                    positionId: response.id || '',
                    location: response.locationName || '',
                    locationId: response.locationId || '',
                    positionsUnderNOC: response.nkdpName || '',
                    nkdpCode: response.nkdpCode || '',
                    nkpdId: response.nkpdId || '',
                    personnelCategory: response.catPersName || response.catPers || '',
                    catPersCode: response.catPersCode || '',
                    workCategory: response.catWorkName || response.catWork || '',
                    workCategoryCode: response.catWorkCode || '',
                    noticePeriod: response.noticePeriod || '',
                    status: response.status || '',
                    nkid: response.nkid || '' // Store nkid from response

                };
                setFormData(newFormData);

                setValue('adminBranch', newFormData.companyBranchId);
                setValue('contractBranchId', newFormData.companyBranchId);
                setValue('empOfficesId', newFormData.costCenterId);
                setValue('hrOrgUnitId', newFormData.structuralGroupId);
                setValue('hrPositionId', newFormData.positionId);
                setValue('locationId', newFormData.locationId);
                setValue('nkdp', newFormData.nkpdId);
                setValue('catPers', newFormData.catPersCode);
                setValue('catWork', newFormData.workCategoryCode);


                // Show the form
                setShowPositions(true);
            }
        } catch (error) {
            console.error('Error fetching position data:', error);
        } finally {
            setLoading(false);
        }
    };

    const submitForm = async (data) => {
        // Clear previous server errors on new submit
        setServerErrors([]);
        try {
            setLoading(true);

            let reasonCode = '';
            let reasonId = '';
            const selectedReasonId = data[reasonFieldName];

            if (selectedReasonId && Array.isArray(majorContracts) && majorContracts.length > 0) {
                const selectedReason = majorContracts.find(
                    (mc) => String(mc.id) === String(selectedReasonId)
                );
                if (selectedReason && selectedReason.reasonCode != null) {
                    reasonCode = String(selectedReason.reasonCode);
                }
                if (selectedReason && selectedReason.reasonId != null) {
                    reasonId = String(selectedReason.reasonId);
                }
            }

            const payload = {
                contractType: contractType,
                ...data,
                reasonCode: reasonCode,
                reasonId: reasonId
            };

            if (person) {
                payload.personId = person.personId || person.id;
                payload.empId = person.recordId;
            }

            // If special reason (004 / 1004) is selected and a holder person is chosen from the table,
            // send that selected person's id as titularId
            if (reasonCode === '004' && String(reasonId) === '1004' && holderPerson && holderPerson.id) {
                payload.titularId = holderPerson.id;
            }

            if (contractType === 'laborContract' || contractType === 'additionalLaborContract') {

                payload.typeDoc = "1"
            }
            if (contractType === 'additionalAgreement') {
                payload.typeDoc = "2"
            }
            if (contractType === 'termination') {
                payload.typeDoc = "3"
                if (selectedMainContract && Array.isArray(mainContracts) && mainContracts.length > 0) {
                    const selectedContract = mainContracts.find(
                        (c) => String(c.contractId) === String(selectedMainContract)
                    );
                    if (selectedContract?.contractBranchId != null) {
                        payload.contractBranchId = selectedContract.contractBranchId;
                    }
                }
            }
            if (contractType === 'employerChange') {
                payload.typeDoc = "9"
            }

            // Add nkid from formData if available
            if (formData.nkid) {
                payload.nkid = formData.nkid;
            }

            if (selectedMainContract) {
                payload.contractSerial = selectedMainContract;
                payload.baseContractId = selectedMainContract;
                payload.pContract = selectedMainContract;
            }

            // Convert "1"/"2" to true/false for backend
            if (payload.memberBoard === '1' || payload.memberBoard === 1) {
                payload.memberBoard = true;
            } else if (payload.memberBoard === '2' || payload.memberBoard === 2) {
                payload.memberBoard = false;
            }

            if (payload.isBlockedSalaryAmount === '1' || payload.isBlockedSalaryAmount === 1) {
                payload.isBlockedSalaryAmount = true;
            } else if (payload.isBlockedSalaryAmount === '2' || payload.isBlockedSalaryAmount === 2) {
                payload.isBlockedSalaryAmount = false;
            }

            if (payload.boardDirectors === '1' || payload.boardDirectors === 1) {
                payload.boardDirectors = true;
            } else if (payload.boardDirectors === '2' || payload.boardDirectors === 2) {
                payload.boardDirectors = false;
            }

            if (editingContractId && contractNo) {
                payload.contractNo = contractNo;
            }

            if (editingContractId) {
                const contractIdToSelect = editingContractId;
                await majorContractsService.updateContract(editingContractId, payload);
                notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');

                setEditingContractId(null);
                setIsCreateModeActive(false);

                const updatedContracts = await majorContractsService.getAdditionalAgreementContractsById(person.recordId);
                setContracts(updatedContracts || []);

                // Load contract by ID directly (not by index)
                await loadContractById(contractIdToSelect, false);
            } else {
                const createResponse = await majorContractsService.createAdditionalAgreement(payload);
                notifySuccess(t('common.createdSuccessfully'));

                setIsCreateModeActive(false);

                const updatedContracts = await majorContractsService.getAdditionalAgreementContractsById(person.recordId);
                setContracts(updatedContracts || []);

                // Get contractId from create response or find the newly created contract
                const newContractId = createResponse?.contractId || createResponse?.id ||
                    (updatedContracts && updatedContracts.length > 0 ? updatedContracts[0].contractId : null);

                if (newContractId) {
                    // Load contract by ID directly (not by index)
                    await loadContractById(newContractId, false);
                }
            }
        } catch (error) {
            console.error('Error saving contract:', error);
            const backendMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');

            let normalized = [];
            if (Array.isArray(backendMessage)) {
                normalized = backendMessage;
            } else if (backendMessage) {
                normalized = String(backendMessage).split(/\r?\n/);
            }

            const cleaned = normalized
                .map(msg => String(msg).trim())
                .filter(msg => msg.length > 0);

            setServerErrors(cleaned);
            openErrorModalAfterScroll();
        } finally {
            setLoading(false);
        }
    };

    const handleMiniEditIconClick = async (item) => {
        // Only prevent clicking when in create mode (not edit mode)
        if (isCreateModeActive && !editingContractId) {
            return;
        }

        const contractId = item?.contractId;

        if (contractId) {
            setEditingContractId(contractId); // Set edit mode
            setIsCreateModeActive(true); // Enable save button

            const index = mappedContracts.findIndex(contract => contract.contractId === contractId);
            if (index >= 0) {
                await handleContractRowClick(index, true); // Pass true to indicate edit mode
            } else {
                console.error('Contract not found in mappedContracts');
            }
        } else {
            console.error('No contractId found in item');
        }
    };


    if (isHolderPickerOpen) {
        return (
            <PersonPicker
                title="Избор на титуляр"
                onSelectPerson={(selected) => {
                    if (selected) {
                        setHolderPerson(selected);
                    }
                    setIsHolderPickerOpen(false);
                }}
                onCancel={() => setIsHolderPickerOpen(false)}
            />
        );
    }

    const handleFormInvalid = () => {
        openErrorModalAfterScroll();
    };

    const hasPositionError = !!errors.hrPositionId;
    const hasStarRequiredErrors = Object.keys(errors).some((key) => key !== 'hrPositionId');

    return (
        //From grid with reason selects
        <form onSubmit={handleSubmit(submitForm, handleFormInvalid)}>
            {showErrorModal && (serverErrors.length > 0 || hasStarRequiredErrors || hasPositionError) && (
                <div className="error-modal-overlay">
                    <div className="error-modal">
                        <div className="error-modal-header">
                            <span className="error-modal-title">
                                {t('common.validationErrors') || 'Възникнаха грешки'}
                            </span>
                            <button
                                type="button"
                                className="error-modal-close"
                                onClick={() => setShowErrorModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="error-modal-body">
                            <ul>
                                {hasStarRequiredErrors && (
                                    <li key="fe-global">
                                        {'Моля попълнете задължителните полета, посочени с звездички.'}
                                    </li>
                                )}
                                {hasPositionError && (
                                    <li key="fe-position">
                                        {'Моля изберете позиция.'}
                                    </li>
                                )}
                                {serverErrors.map((msg, idx) => (
                                    <li key={`se-${idx}`}>{msg}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="error-modal-footer">
                            <button
                                type="button"
                                className="form-btn btn-blue"
                                onClick={() => setShowErrorModal(false)}
                            >
                                {t('common.continue') || 'Продължение'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="position-center mb-10" >
                <div className="form-grid">
                    <span>{t('persons.personContract.reason')}<span className="required">*</span></span>
                    <select
                        className="small-select"
                        style={{ width: '600px' }}
                        disabled={!isEditMode}
                        {...register(reasonFieldName, {
                            required: true,
                        })}
                        onChange={(e) => {
                            const selectedValue = e.target.value;
                            setValue(reasonFieldName, selectedValue);

                            if (selectedValue === '' || !selectedValue) {
                                setReasonNAP('');
                                setValue('expiredContract', '2');
                                setIsExpiredContractEditable(false);
                                // Clear tracked reason so we fall back to adminBranch company
                                setReasonCode('');
                                setReasonId('');
                            } else {
                                const selectedContract = majorContracts.find(mc =>
                                    mc.id === Number(selectedValue) || String(mc.id) === String(selectedValue)
                                );
                                const hrNssiValue = selectedContract?.hrNssiReasons || '';
                                setReasonNAP(hrNssiValue);
                                const isFixedTermReason = selectedContract?.description === '2 Срочен трудов договор по чл.68,ал.1,т.1 КТ';
                                setValue('expiredContract', isFixedTermReason ? '1' : '2');
                                setIsExpiredContractEditable(isFixedTermReason);

                                // Track selected reasonCode / reasonId for companies rule
                                const code = selectedContract && selectedContract.reasonCode != null
                                    ? String(selectedContract.reasonCode).padStart(3, '0')
                                    : '';
                                const id = selectedContract && selectedContract.reasonId != null
                                    ? String(selectedContract.reasonId)
                                    : '';
                                setReasonCode(code);
                                setReasonId(id);
                            }
                        }}
                    >
                        <option value="">{t('common.select') || 'Select'}</option>
                        {majorContracts.map(majorContract => (
                            <option key={majorContract.id} value={majorContract.id}>{majorContract.description}</option>
                        ))}
                    </select>
                    <span>{t('persons.personContract.reasonNAP')}<span className="required">*</span></span>
                    <input type="text" className="small-input" style={{ width: '600px' }} value={reasonNAP} readOnly />
                    <span>{t('persons.personContract.documentNumber')}</span>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <input disabled={true} type="text" className="small-input" style={{ width: '100px' }} value={contractNo} {...register('YOUR_DOCUMENT_NUMBER_FIELD_NAME')} />
                        <span>{t('persons.personContract.documentDate')}<span className="required">*</span></span>
                        <CustomDatePicker
                            name="documentDate"
                            value={documentDate}
                            onChange={(date) => {
                                setDocumentDate(date);
                                setValue('contractDate', date);
                            }}
                            isSmall={true}
                            disabled={!isEditMode}
                        />
                        <input
                            type="hidden"
                            {...register('contractDate', { required: true })}
                        />

                        <span>{t('persons.personContract.fromDate')}<span className="required">*</span></span>
                        <CustomDatePicker
                            name="fromDate"
                            value={fromDate}
                            onChange={(date) => {
                                setFromDate(date);
                                setValue('effectiveDate', date);
                            }}
                            isSmall={true}
                            disabled={!isEditMode}
                        />
                        <input
                            type="hidden"
                            {...register('effectiveDate', { required: true })}
                        />
                    </div>
                </div>
            </div>
            <div className="flex-row align-stretch justify-content-center">
                {contractType !== "termination" && (
                    <>
                        <div className="flex-column">
                            <div className="flex-row">
                                <span className="form-label no-wrap" style={{ width: '220px' }}> {t('persons.personContract.testContract')} </span>
                                <select className="small-select" style={{ width: '100px' }} disabled={!isEditMode} {...register('testContract')}>
                                    <option value="1">{t('common.yes')}</option>
                                    <option value="2">{t('common.no')}</option>
                                </select>
                            </div>
                            <div className="flex-row">
                                <span className="form-label no-wrap" style={{ width: '220px' }}> {t('persons.personContract.expiredContract')} </span>
                                <select disabled={!isExpiredContractEditable} value={watch('expiredContract') ?? '2'} className="small-select" style={{ width: '100px' }} {...register('expiredContract')}>
                                    <option value="1">{t('common.yes')}</option>
                                    <option value="2">{t('common.no')}</option>
                                </select>

                            </div>

                            <div className="flex-row">
                                <span className="form-label no-wrap" style={{ width: '220px' }}> {t('persons.personContract.omzInEuro')}<span className="required">*</span></span>
                                <input
                                    type="number"
                                    className="small-input"
                                    style={{ width: '100px' }}
                                    disabled={!isEditMode}
                                    {...register('salary', {
                                        valueAsNumber: true,
                                        required: true,
                                    })}
                                />
                            </div>
                            <div className="flex-row">
                                <span className="form-label no-wrap" style={{ width: '220px' }}> {t('persons.personContract.workExperiencePercentage')}<span className="required">*</span></span>
                                <input
                                    type="number"
                                    className="small-input"
                                    style={{ width: '100px' }}
                                    disabled={!isEditMode}
                                    {...register('prcntClass', {
                                        required: true,
                                    })}
                                />
                            </div>
                        </div>
                        <div className="flex-column justify-flex-end">
                            {(watch('expiredContract') ?? '2') === '1' && (
                                <div className="flex-row">
                                    <span className="form-label no-wrap" style={{ width: '330px' }}> {t('persons.personContract.expiredContractDate')}: </span>
                                    <input type="date" className="small-input" style={{ width: '130px' }} disabled={!isEditMode} {...register('expiredContractDate')} />
                                </div>
                            )}
                            <div className="flex-row">
                                <span className="form-label no-wrap" style={{ width: '330px' }}>{t('persons.personContract.workHours')}<span className="required">*</span></span>
                                <input
                                    type="number"
                                    className="small-input"
                                    style={{ width: '130px' }}
                                    disabled={!isEditMode}
                                    {...register('durationDays', {
                                        required: true,
                                    })}
                                />
                            </div>
                            <div className="flex-row">
                                <span className="form-label no-wrap" style={{ width: '330px' }}>{t('persons.personContract.ukContract')}</span>
                                <select className="small-select" style={{ width: '130px' }}
                                    disabled={!isEditMode} {...register('memberBoard')}>
                                    <option value="1">{t('common.yes')}</option>
                                    <option value="2">{t('common.no')}</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex-column">
                            {contractType !== 'employerChange' && (
                                <>
                                    <div className="flex-row">
                                        <span className="form-label no-wrap" style={{ width: '300px' }}>{t('persons.personContract.paidAnnualLeave')}<span className="required">*</span></span>
                                        <input
                                            type="number"
                                            className="small-input"
                                            style={{ width: '100px' }}
                                            disabled={!isEditMode}
                                            {...register('daysAgreedPaidAnnualLeave', {
                                                required: true,
                                            })}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="flex-row">
                                <span className="form-label no-wrap" style={{ width: '300px' }}>{t('persons.personContract.reminderMessages')}</span>
                                <select className="small-select" style={{ width: '100px' }}
                                    disabled={!isEditMode} {...register('isBlockedSalaryAmount')}>
                                    <option value="1">{t('common.yes')}</option>
                                    <option value="2">{t('common.no')}</option>
                                </select>
                            </div>
                            <div className="flex-row">
                                <span className="form-label no-wrap" style={{ width: '300px' }}>{t('persons.personContract.boardMember')}</span>
                                <select className="small-select" style={{ width: '100px' }}
                                    disabled={!isEditMode} {...register('boardDirectors')}>
                                    <option value="1">{t('common.yes')}</option>
                                    <option value="2">{t('common.no')}</option>
                                </select>
                            </div>
                        </div>
                    </>
                )}
                {contractType === "termination" && (
                    <>
                        <div className="flex-column gap-2">
                            <div className="flex-row">
                                <span className="no-wrap">{t('persons.personContract.compensation2222')}</span>
                                <input type="date" className="small-input"
                                    disabled={!isEditMode} {...register('datePaidCompensation222_2')} />
                                <span className="no-wrap">{t('persons.personContract.compensation2223')}</span>
                                <input type="date" className="small-input"
                                    disabled={!isEditMode} {...register('datePaidCompensation222_3')} />
                                <span className="no-wrap">{t('persons.personContract.reminderMessages')}</span>
                                <select className="small-select" style={{ width: '100px' }}
                                    disabled={!isEditMode} {...register('isBlockedSalaryAmount')}>
                                    <option value="1">{t('common.yes')}</option>
                                    <option value="2">{t('common.no')}</option>
                                </select>
                            </div>
                            <div className="flex-row position-center">
                                <span className="no-wrap" >{t('persons.personContract.paidAnnualLeaveDays')}</span>
                                <input type="number" className="small-input" style={{ width: '20%' }} {...register('usedVacationDays')} />
                            </div>
                        </div>
                    </>
                )}
            </div>
            {/* Positions container */}
            <div className="flex-row position-center">
                <div className="flex-column width-100">
                    {showPositions && contractType !== "termination" && (
                        <div className="flex-row position-center">
                            <button className="btn-blue" style={{ width: '50%' }} disabled={!isEditMode} onClick={() => setShowPositions(false)}>{t('persons.personContract.selectionOfPosition')}</button>
                        </div>
                    )}
                    {/* Positions data */}
                    {showPositions && (
                        <>
                            <div className="flex-row position-center">
                                <div className="form-grid mt-2 width-50 position-form-grid">
                                    {contractType !== "termination" && (
                                        <>
                                            <span> {t('persons.personContract.companyBranch')}</span>
                                            <input type="text" className="small-input" value={formData.companyBranch} readOnly />
                                            <input type="hidden" {...register('adminBranch')} />
                                            <input type="hidden" {...register('contractBranchId')} />

                                            <span> {t('persons.personContract.costCenter')}</span>
                                            <input type="text" className="small-input" value={formData.costCenter} readOnly />
                                            <input type="hidden" {...register('empOfficesId')} />

                                            <span> {t('persons.personContract.structuralGroup')}</span>
                                            <input type="text" className="small-input" value={formData.structuralGroup} readOnly />
                                            <input type="hidden" {...register('hrOrgUnitId')} />

                                            <span className="no-wrap"> {t('persons.personContract.positionInTheCompany')}</span>
                                            <input type="text" className="small-input" value={formData.positionInTheCompany} readOnly />
                                            <input
                                                type="hidden"
                                                {...register('hrPositionId', {
                                                    validate: (value) => {
                                                        if (contractType === 'termination') return true;
                                                        // In edit mode backend may return name without id; accept that as existing selection.
                                                        return !!value || !!formData.positionInTheCompany;
                                                    },
                                                })}
                                            />

                                            <span> {t('persons.personContract.location')}</span>
                                            <input type="text" className="small-input" value={formData.location} disabled={!isEditMode} />
                                            <input type="hidden" {...register('locationId')} />

                                            <span className="no-wrap"> {t('persons.personContract.positionsUnderNOC')}</span>
                                            <input type="text" className="small-input" value={formData.positionsUnderNOC} readOnly />
                                            <input type="hidden" {...register('nkdp')} />

                                            <span> {t('persons.personContract.personnelCategory')}</span>
                                            <input type="text" className="small-input" value={formData.personnelCategory} readOnly />
                                            <input type="hidden" {...register('catPers')} />

                                            <span> {t('persons.personContract.workCategory')}</span>
                                            <input type="text" className="small-input" value={formData.workCategory} readOnly />
                                            <input type="hidden" {...register('catWork')} />

                                            <span> {t('persons.personContract.insuaranceType')}</span>
                                            <select className="small-select" value={formData.insuaranceType} disabled={!isEditMode} {...register('kindInsuranceId')} onChange={(e) => { setFormData({ ...formData, insuaranceType: e.target.value }); setValue('kindInsuranceId', e.target.value); }}>
                                                <option value="">{t('common.select') || 'Select'}</option>
                                                {typeOfInsurance.map(typeOfInsurance => (
                                                    <option key={typeOfInsurance.id} value={typeOfInsurance.id}> {typeOfInsurance.value} - {typeOfInsurance.note}</option>
                                                ))}
                                            </select>

                                            <span className="no-wrap">{t('persons.personContract.noticePeriod')}</span>
                                            <select className="small-select" value={formData.noticePeriod} disabled={!isEditMode} {...register('hrTerminationNotePeriodId')} onChange={(e) => { setFormData({ ...formData, noticePeriod: e.target.value }); setValue('hrTerminationNotePeriodId', e.target.value); }}>
                                                <option value="">{t('common.select') || 'Select'}</option>
                                                {noticePeriods.map(noticePeriod => (
                                                    <option key={noticePeriod.id} value={noticePeriod.id}> {noticePeriod.code} - {noticePeriod.description}</option>
                                                ))}
                                            </select>

                                            {reasonCode === '004' && String(reasonId) === '1004' && (
                                                <>
                                                    <span className="no-wrap">Титуляр на позицията</span>
                                                    <div className="flex-row">
                                                        <input
                                                            type="text"
                                                            className="small-input"
                                                            disabled={!isEditMode}
                                                            readOnly
                                                            value={
                                                                holderPerson
                                                                    ? [holderPerson.name, holderPerson.fathersName, holderPerson.surname]
                                                                        .filter(Boolean)
                                                                        .join(' ')
                                                                    : ''
                                                            }
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn-blue"
                                                            disabled={!isEditMode}
                                                            onClick={() => {
                                                                setIsHolderPickerOpen(true);
                                                                // initial load
                                                                getHolderPersons({
                                                                    searchQuery: holderSearchQuery,
                                                                    searchType: holderSearchType,
                                                                    pageNumber: 0,
                                                                    pageSize: holderPageSize,
                                                                });
                                                            }}
                                                        >
                                                            ...
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            <span> {t('persons.personContract.status')}</span>
                                            <select className="small-select" value={formData.status} disabled={!isEditMode} {...register('statut')} onChange={(e) => { setFormData({ ...formData, status: e.target.value }); setValue('statut', e.target.value); }}>
                                                <option value="">{t('common.select') || 'Select'}</option>
                                                <option value="EMPLOYEE">EMPLOYEE</option>
                                                <option value="Член НС/УС">Член НС/УС</option>
                                                <option value="EXPAT">EXPAT</option>
                                                <option value="IMPAT">IMPAT</option>
                                                <option value="EXPAT_IMPAT">EXPAT_IMPAT</option>
                                                <option value="Стажант">Стажант</option>
                                            </select>

                                        </>
                                    )}

                                    <span> {t('persons.personContract.signatures1')}</span>
                                    <select className="small-select" value={formData.signatures1} disabled={!isEditMode} {...register('hrLabourDocSignBySignatoryId')} onChange={(e) => { setFormData({ ...formData, signatures1: e.target.value }); setValue('hrLabourDocSignBySignatoryId', e.target.value); }}>
                                        <option value="">{t('common.select') || 'Select'}</option>
                                        {signees.map(signee => (
                                            <option key={signee.id} value={signee.id}> {signee.name} - {signee.surname}</option>
                                        ))}
                                    </select>

                                    <span> {t('persons.personContract.signatures2')}</span>
                                    <select className="small-select" value={formData.signatures2} disabled={!isEditMode} {...register('hrLabourDocSignBySignatoryId2')} onChange={(e) => { setFormData({ ...formData, signatures2: e.target.value }); setValue('hrLabourDocSignBySignatoryId2', e.target.value); }}>
                                        <option value="">{t('common.select') || 'Select'}</option>
                                        {signees.map(signee => (
                                            <option key={signee.id} value={signee.id}> {signee.name} - {signee.surname}</option>
                                        ))}
                                    </select>

                                    <span> {t('persons.personContract.positionType')}</span>
                                    <select className="small-select" value={formData.positionType} disabled={!isEditMode} {...register('positionTypeId')} onChange={(e) => { setFormData({ ...formData, positionType: e.target.value }); setValue('positionTypeId', e.target.value); }}>
                                        <option value="">{t('common.select') || 'Select'}</option>
                                        {nomenclatures.map(nomenclature => (
                                            <option key={nomenclature.nomenclatureItem?.id || nomenclature.id} value={nomenclature.nomenclatureItem?.id || nomenclature.id}>{nomenclature.name}</option>
                                        ))}
                                    </select>

                                </div>
                            </div>
                            <div className="form-controls">
                                <LoadingButton
                                    type="submit"
                                    className="form-btn btn-blue"
                                    loading={loading}
                                    disabled={showErrorModal || (!isCreateModeActive && !editingContractId)}
                                >
                                    {t('common.save')}
                                </LoadingButton>
                                <button type="button" className="form-btn btn-red" onClick={onClose} loading={loading} disabled={showErrorModal}>
                                    {t('common.exit')}
                                </button>
                            </div>

                        </>
                    )}
                    {!showPositions && (
                        <Table
                            onPositionIconClick={currentView === 'structuralGroups' ? handlePositionIconClick : (currentView === 'positions' ? handlePositionRowIconClick : handleBackToStructuralGroups)}
                            columns={currentView === 'positions' ? positionsColumns : structuralGroupsColumns}
                            showOrgUnitIcon={currentView === 'positions'}
                            onOrgUnitIconClick={handleBackToStructuralGroups}
                            data={currentView === 'positions' ? positionsData : (isDrillDownMode ? drillDownData : structuralGroups)}
                            loading={currentView === 'positions' ? positionsLoading : structuralGroupsLoading}
                            selectedData={currentView === 'positions' ? null : checkboxSelectedRecord?.id}
                            getData={() => { }}
                            showCompanySelect={currentView === 'structuralGroups'}
                            showPositionIcon={currentView === 'structuralGroups' && isDrillDownMode}
                            showPositionIconInRows={currentView === 'positions'}
                            companyOptions={companyOptions}
                            showLevelSelect={currentView === 'structuralGroups'}
                            searchOptions={searchOptions}
                            companiesLoading={companiesLoading}
                            showPageSizeSelector={false}
                            showPagination={false}
                            showPreviousPage={currentView === 'structuralGroups' && selectedRecord ? false : true}
                            selectedCompany={selectedCompany}
                            setSelectedCompany={handleCompanyChange}
                            ignoreAdminBranchFilter={showAllCompanies}
                            selectedLevel={selectedLevel}
                            setSelectedLevel={handleLevelChange}
                            levelOptions={levels}
                            levelsLoading={levelsLoading}
                            handleCheckboxChange={currentView === 'positions' ? undefined : handleCheckboxChange}
                            handleRowClick={currentView === 'positions' ? undefined : handleRowClick}
                            showBreadcrumb={currentView === 'structuralGroups' && isDrillDownMode}
                            breadcrumbPath={currentView === 'structuralGroups' && isDrillDownMode ? [
                                {
                                    id: 'root',
                                    title: (() => {
                                        const levelTitle = selectedLevelObject?.longDescription || selectedLevelObject?.name;
                                        return levelTitle || 'Root';
                                    })()
                                },
                                ...breadcrumbPath
                            ] : []}
                            onBreadcrumbClick={currentView === 'structuralGroups' && isDrillDownMode ? handleBreadcrumbClick : null}
                        />
                    )}
                    {!showPositions && (
                        <div className="form-controls">
                            <button type="button" className="form-btn btn-red" onClick={() => setShowPositions(true)} loading={loading}>
                                {t('common.cancel')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex-no-gap">
                <Table
                    data={mappedContracts}
                    columns={columns}
                    loading={contractsLoading}
                    showPagination={false}
                    showPageSizeSelector={false}
                    showPreviousPage={false}
                    showActiveSelect={false}
                    showCountriesSelect={false}
                    showNomenclaturesSelect={false}
                    showCustomSearch={true}
                    showLevelSelect={false}
                    showCompanySelect={false}
                    handleRowClick={isCreateModeActive && !editingContractId ? null : handleContractRowClick}
                    showMiniEditIcon={!isCreateModeActive || !!editingContractId}
                    onMiniEditIconClick={(!isCreateModeActive || !!editingContractId) ? handleMiniEditIconClick : null}
                />
            </div>

        </form>
    );
}